import { Component, type ErrorInfo, type JSX, type ReactNode, useEffect } from "react";
import { useRouteError } from "react-router";

import { isChunkLoadError } from "@/lib/chunk-load-error";
import { cn } from "@/lib/utils";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
}

// A failed code-chunk load means this tab runs a previous build (navigation
// spec, "App shell recovers from render errors instead of blanking").
const FALLBACK_COPY = {
  generic: {
    eyebrow: "Error de interfaz",
    heading: "Algo salió mal",
    body: "Puedes recargar la página o volver al inicio para continuar trabajando.",
  },
  newVersion: {
    eyebrow: "Actualización disponible",
    heading: "Hay una versión nueva",
    body: "Esta pestaña sigue en una versión anterior de la aplicación. Recarga la página para continuar con la versión más reciente.",
  },
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  override componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    console.error("Render error caught by ErrorBoundary", {
      message: getErrorMessage(error),
      componentStack: errorInfo.componentStack,
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <RecoverableErrorFallback className="min-h-screen" isChunkError={this.state.isChunkError} />
      );
    }
    return this.props.children;
  }
}

export function RouteErrorFallback(): JSX.Element {
  const error = useRouteError();

  useEffect(() => {
    console.error("Route render error caught", { message: getErrorMessage(error) });
  }, [error]);

  return <RecoverableErrorFallback isChunkError={isChunkLoadError(error)} />;
}

function RecoverableErrorFallback({
  className,
  isChunkError,
}: { className?: string; isChunkError: boolean }): JSX.Element {
  const copy = isChunkError ? FALLBACK_COPY.newVersion : FALLBACK_COPY.generic;
  return (
    <section
      role="alert"
      className={cn("grid place-items-center bg-background px-4 py-12 text-foreground", className)}
    >
      <div className="w-full max-w-md rounded-md border border-border bg-card p-6 text-card-foreground shadow-sm">
        <p className="text-label-2 uppercase text-muted-foreground">{copy.eyebrow}</p>
        <h1 className="mt-2 text-heading-2">{copy.heading}</h1>
        <p className="mt-3 text-body-2 text-muted-foreground">{copy.body}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary px-4 py-2 text-body-2 font-medium text-primary-foreground hover:bg-primary/90"
          >
            recargar
          </button>
          <a
            href="/"
            className="rounded-md border border-border px-4 py-2 text-body-2 font-medium text-foreground hover:bg-muted"
          >
            volver al inicio
          </a>
        </div>
      </div>
    </section>
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
